import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Sidebar from "@/components/Sidebar";
import BottomTabBar from "@/components/BottomTabBar";
import LandingPage from "@/pages/LandingPage";
import FeaturesPage from "@/pages/FeaturesPage";
import PricingPage from "@/pages/PricingPage";
import SupportPage from "@/pages/SupportPage";
import LoginPage from "@/pages/LoginPage";
import GetStartedPage from "@/pages/GetStartedPage";
import HomePage from "@/pages/HomePage";
import { GamesListPage, GameDetailPage } from "@/pages/GamesPage";
import ClipsPage from "@/pages/ClipsPage";
import HighlightsPage from "@/pages/HighlightsPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import PendingApprovalPage from "@/pages/PendingApprovalPage";
import MessagesPage from "@/pages/MessagesPage";
import PaywallPage from "@/pages/PaywallPage";
import GDOLoginPage from "@/pages/GDOLoginPage";
import GDOPortalPage from "@/pages/GDOPortalPage";
import InvitePage from "@/pages/InvitePage";
import SharedHighlightPage from "@/pages/SharedHighlightPage";
import MainframePage from "@/pages/MainframePage";
import GameUploadPage from "@/pages/GameUploadPage";
import CareersPage from "@/pages/CareersPage";
import CommunityPage from "@/pages/CommunityPage";
import LeagueOrgSignupPage from "@/pages/LeagueOrgSignupPage";
import PermissionPage from "@/pages/PermissionPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ParentClaimPage from "@/pages/ParentClaimPage";
import YSVLogisticsPage from "@/pages/YSVLogisticsPage";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";

const LoadingSpinner = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--dark-bg)' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid var(--dark-border)', borderTopColor: 'var(--cyan)', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
      <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

// Shown when the bootstrap /auth/me couldn't reach the backend. The token is
// still on disk, so bouncing to /login would throw away a session that is very
// likely still valid — offer a retry instead.
const ConnectionErrorScreen = ({ onRetry }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--dark-bg)', padding: 20 }}>
    <div style={{ textAlign: 'center', maxWidth: 420 }} data-testid="auth-unreachable">
      <h2 style={{ color: 'var(--text-primary)', fontSize: 20, marginBottom: 8 }}>Couldn't reach the server</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
        You're still signed in. Check your connection and try again.
      </p>
      <button onClick={onRetry} style={{
        background: 'var(--cyan)', color: '#04121a', border: 'none', borderRadius: 8,
        padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
      }}>Try again</button>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading, authError, retryAuth } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user && authError) return <ConnectionErrorScreen onRetry={retryAuth} />;
  if (!user) return <Navigate to="/login" replace />;
  // Check approval status
  if (user.approval_status === "pending" && user.role !== "ysv_admin") {
    return <PendingApprovalPage />;
  }
  if (user.approval_status === "rejected") {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const ALWAYS_FREE_ROLES = ["ysv_admin", "league_admin", "gdo"];

const STAFF_ROLES = ["ysv_admin", "league_coordinator", "hub_coordinator", "gdo_coordinator"];

const StaffRoute = ({ children }) => {
  // Route-level role guard for /ysv-logistics. Without this, a non-staff
  // authenticated user (player_parent / coach / org_admin) who direct-types
  // the URL would mount YSVLogisticsPage, fire its data calls, get back 403s,
  // and trigger a React dev runtime-error overlay (caught by iter-23 testing).
  const { user, loading, authError, retryAuth } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user && authError) return <ConnectionErrorScreen onRetry={retryAuth} />;
  if (!user) return <Navigate to="/login" replace />;
  if (!STAFF_ROLES.includes(user.role)) {
    return <Navigate to="/profile" replace />;
  }
  return children;
};

const SubscriptionRoute = ({ children }) => {
  const { user, loading, api, authError, retryAuth } = useAuth();
  const [accessState, setAccessState] = React.useState({ checked: false, has_access: false });

  React.useEffect(() => {
    if (!user) return;
    // Fast-path: always-free roles + paid subscription don't need backend round-trip
    if (ALWAYS_FREE_ROLES.includes(user.role) || user.subscription_status === "active") {
      setAccessState({ checked: true, has_access: true });
      return;
    }
    // Otherwise ask the backend for threshold-based eligibility (org_admin, coach)
    api().get("/subscription/status")
      .then(r => setAccessState({ checked: true, has_access: !!r.data?.has_access, detail: r.data }))
      .catch(() => setAccessState({ checked: true, has_access: false }));
  }, [user, api]);

  if (loading) return <LoadingSpinner />;
  if (!user && authError) return <ConnectionErrorScreen onRetry={retryAuth} />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.approval_status === "pending" && user.role !== "ysv_admin") return <PendingApprovalPage />;
  if (!accessState.checked) return <LoadingSpinner />;
  if (accessState.has_access) return children;
  return <AppLayout><PaywallPage /></AppLayout>;
};

const AppLayout = ({ children }) => {
  React.useEffect(() => {
    document.body.classList.add("ysv-with-bottom-tabbar");
    return () => document.body.classList.remove("ysv-with-bottom-tabbar");
  }, []);
  return (
    <div style={{ minHeight: '100vh' }}>
      <Sidebar />
      <main data-testid="app-main" style={{ minHeight: '100vh', paddingTop: 'calc(60px + env(safe-area-inset-top, 0px))', background: 'var(--dark-bg)' }}>
        {children}
      </main>
      <BottomTabBar />
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const publicPaths = ['/landing', '/features', '/pricing', '/support', '/careers', '/community', '/permission', '/login', '/sign-in', '/get-started', '/league-signup', '/gdo', '/invite', '/share', '/forgot-password', '/reset-password', '/parent-claim'];
  const isPublic = publicPaths.some(p => location.pathname.startsWith(p));

  if (isPublic) {
    const showNavFooter = ['/landing', '/features', '/pricing', '/support', '/community', '/get-started'].some(p => location.pathname.startsWith(p));
    const isGdo = location.pathname.startsWith('/gdo');
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {showNavFooter && !isGdo && <Navbar />}
        <main style={{ flex: 1 }}>
          <Routes>
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign-in" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/parent-claim" element={<ParentClaimPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/league-signup" element={<LeagueOrgSignupPage />} />
            <Route path="/permission" element={<PermissionPage />} />
            <Route path="/gdo" element={<GDOLoginPage />} />
            <Route path="/gdo/portal" element={<GDOPortalPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/share/highlight/:id" element={<SharedHighlightPage />} />
          </Routes>
        </main>
        {showNavFooter && !isGdo && <Footer />}
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/landing" replace />} />
      <Route path="/home" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/games" element={<SubscriptionRoute><AppLayout><GamesListPage /></AppLayout></SubscriptionRoute>} />
      <Route path="/games/new" element={<SubscriptionRoute><AppLayout><GameUploadPage /></AppLayout></SubscriptionRoute>} />
      <Route path="/games/:gameId" element={<SubscriptionRoute><AppLayout><GameDetailPage /></AppLayout></SubscriptionRoute>} />
      <Route path="/clips" element={<SubscriptionRoute><AppLayout><ClipsPage /></AppLayout></SubscriptionRoute>} />
      <Route path="/highlights" element={<SubscriptionRoute><AppLayout><HighlightsPage /></AppLayout></SubscriptionRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/mainframe" element={<ProtectedRoute><AppLayout><MainframePage /></AppLayout></ProtectedRoute>} />
      <Route path="/ysv-logistics" element={<StaffRoute><AppLayout><YSVLogisticsPage /></AppLayout></StaffRoute>} />
      <Route path="/messages" element={<ProtectedRoute><AppLayout><MessagesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/landing" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <InstallPWAPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
